import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

interface DiscrepancyChunkCompleted {
  userId: string;
  jobId: string;
  syncIds: string[];
  result: { synced_items: any[]; discrepancies: any[]; total: number };
}

interface DiscrepancyChunkFailed {
  userId: string;
  jobId: string;
  syncIds: string[];
  error: string;
}

interface UseDiscrepancyStreamArgs {
  userId: string | number;
  accountId?: string | number;
  onChunk: (data: DiscrepancyChunkCompleted) => void;
  onFailure: (data: DiscrepancyChunkFailed) => void;
  // Fired on initial connect AND reconnect — the catch-up hook. Room emits are
  // not buffered, so any completion fired before this socket joined the room is
  // lost; the consumer reconciles in-flight jobs here.
  onConnect?: () => void;
  onReconnect?: () => void;
  enabled?: boolean;
}

// Subscribes to the ms-utils notification socket and forwards discrepancy
// completion/failure events addressed to this user. Mirrors useErrorWebsocketService.
const useDiscrepancyStream = ({
  userId,
  accountId,
  onChunk,
  onFailure,
  onConnect,
  onReconnect,
  enabled = true,
}: UseDiscrepancyStreamArgs) => {
  // Keep latest callbacks in refs so the socket effect does not reconnect on every render.
  const onChunkRef = useRef(onChunk);
  const onFailureRef = useRef(onFailure);
  const onConnectRef = useRef(onConnect);
  const onReconnectRef = useRef(onReconnect);
  onChunkRef.current = onChunk;
  onFailureRef.current = onFailure;
  onConnectRef.current = onConnect;
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!enabled || !userId) return;

    const url = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`;
    const socket: Socket = io(url);

    // Both listeners gate on this single predicate: does the incoming event
    // actually belong to the user this hook is subscribed for? The server only
    // emits to the `user:<id>` room, but this client-side guard is defense in
    // depth against a misrouted emit mutating the wrong user's board state.
    const isForThisUser = (incomingUserId: string | number | undefined): boolean => {
      if (String(incomingUserId) === String(userId)) return true;

      return false;
    };

    socket.on("discrepancy_chunk_completed", (data: DiscrepancyChunkCompleted) => {
      if (isForThisUser(data?.userId)) onChunkRef.current(data);
    });

    socket.on("discrepancy_chunk_failed", (data: DiscrepancyChunkFailed) => {
      if (isForThisUser(data?.userId)) onFailureRef.current(data);
    });

    // Catch-up on initial connect: a chunk may have completed before this
    // socket joined the room, and room emits are not buffered. The consumer
    // reconciles in-flight jobs against getJobStatus here.
    socket.on("connect", () => {
      onConnectRef.current?.();
    });

    socket.io.on("reconnect", () => {
      onReconnectRef.current?.();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId, enabled]);
};

export default useDiscrepancyStream;
